import { useMemo } from 'react';

import { FileTypeName } from 'api/types/entity';
import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate, lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  PersonalDataSection,
  CitizenshipSection,
  ContactsSection,
  PepConnectionSection,
  TaxResidencySection,
  RegistrationAddressSection,
  ResidentialAddressSection,
  AdditionalDocumentsSection,
  KYCSection,
  LegalClientHeadPositionSection,
  PlaceOfBirthSection,
  ActivitySection,
  IdentityDocumentSection,
} from 'modules/client/common/components/sections';
import {
  CountryRiskField,
  DocumentsTableFeature,
} from 'modules/client/common/feature';

import { LegalClientFirstHeadSectionsProps } from './types';

export const useLegalClientFirstHeadSections = ({
  countries,
  countriesLoading,
  kycData,
  mainInfo,
  hideActivitySection,
}: LegalClientFirstHeadSectionsProps) => {
  const { t } = useTranslation();

  const isFinance = isFinanceProject();
  const isGlobal = isGlobalProject();

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.legal.legalClientFirstHead.sections.main'),
          id: 'main',
          content: (
            <>
              <PersonalDataSection />
              <CitizenshipSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              {isGlobal && (
                <DocumentsTableFeature
                  fileType={FileTypeName.IdentityDocument}
                  name="identityDocument.files"
                  title={t(
                    'client.legal.legalClientFirstHead.identityDocument.title'
                  )}
                  titleVariant="subtitle"
                />
              )}
              {isFinance && (
                <IdentityDocumentSection
                  enabledDocuments
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <TaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              {isGlobal && (
                <PlaceOfBirthSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
            </>
          ),
          formSectionKeys: [
            'personalData',
            'citizenships',
            'identityDocument',
            'taxResidences',
            'placeOfBirth',
          ],
        },
        {
          label: t('client.legal.legalClientFirstHead.sections.managerInfo'),
          id: 'managerInfo',
          content: <LegalClientHeadPositionSection onlyPosition />,
          formSectionKeys: ['position'],
        },
        {
          label: t(
            'client.legal.legalClientFirstHead.sections.contactsAndAddresses'
          ),
          id: 'contactsAndAddresses',
          content: (
            <>
              {isFinance && (
                <RegistrationAddressSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <ResidentialAddressSection
                enabledFiles
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <ContactsSection hidePhoneField />
            </>
          ),
          formSectionKeys: [
            'contacts',
            'registrationAddress',
            'residentialAddress',
          ],
        },
        {
          label: t('client.legal.legalClientFirstHead.sections.documents'),
          id: 'documents',
          content: <AdditionalDocumentsSection />,
          formSectionKeys: ['additionalDocuments'],
        },
        {
          label: t('client.legal.legalClientFirstHead.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              {isFinance && <KYCSection kycData={kycData} />}
              <PepConnectionSection notRequestInformation={isGlobal} />
            </>
          ),
          formSectionKeys: ['pepConnection', 'kyc'],
        },
        isFinance &&
          !hideActivitySection && {
            label: t('client.legal.legalClientLegalHead.sections.other'),
            id: 'other',
            content: <ActivitySection isBaseForm />,
            formSectionKeys: ['activity'],
          },
      ]),
    [
      t,
      countries,
      countriesLoading,
      isGlobal,
      isFinance,
      kycData,
      hideActivitySection,
    ]
  );

  const info = useMemo(
    () =>
      mainInfo
        ? [
            lodash.compact([
              isGlobal && {
                label: t(
                  'client.legal.legalClientFirstHead.mainInfo.citizenship'
                ),
                value: mainInfo.citizenship?.name,
                countryRisk: {
                  country: mainInfo.citizenship,
                  field: CountryRiskField.Citizenship,
                },
              },
              {
                label: t('client.legal.legalClientFirstHead.mainInfo.position'),
                value: mainInfo.position,
              },
            ]),
            [
              {
                label: t('client.legal.legalClientFirstHead.mainInfo.email'),
                value: mainInfo.email,
              },
            ],
            [
              {
                label: t('client.legal.legalClientFirstHead.mainInfo.country'),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: mainInfo.countryRiskField,
                },
              },
            ],
            [
              {
                label: t(
                  'client.legal.legalClientFirstHead.mainInfo.dateOfExpiration'
                ),
                value: mainInfo.dateOfExpiration
                  ? formatToDate(mainInfo.dateOfExpiration)
                  : undefined,
              },
            ],
          ]
        : undefined,
    [isGlobal, mainInfo, t]
  );

  return { tabs, info };
};
